import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Global in-flight-request activity store. A single counter tracks how many
 * HTTP requests are currently underway across BOTH axios clients (the
 * authenticated `httpClient` and the unauthenticated `iamPublic`). The axios
 * interceptors call `start()` when a request leaves and `done()` when it
 * settles (success OR error), so the counter reflects real network activity
 * with one instrumentation point per client.
 *
 * `GlobalProgressBar.vue` reads `isActive` to drive the top progress bar.
 *
 * The store is intentionally tiny and pure-logic (no timers, no DOM) so it
 * unit-tests cleanly. The debounce / trickle / min-visible timing lives in
 * `useProgressController`, not here.
 *
 * Setup-store style to match `src/stores/errorDialog.ts`.
 */
export const useRequestActivityStore = defineStore('requestActivity', () => {
  const pendingCount = ref(0);

  /** True while at least one request is in flight. */
  const isActive = computed(() => pendingCount.value > 0);

  /**
   * Mark one request as started. Increments the in-flight counter.
   */
  function start(): void {
    pendingCount.value += 1;
  }

  /**
   * Mark one request as settled (resolved or rejected). Decrements the
   * in-flight counter, but never below zero — a defensive floor that keeps
   * the bar from getting "stuck negative" if a stray `done()` ever runs
   * without a matching `start()`.
   */
  function done(): void {
    pendingCount.value = Math.max(0, pendingCount.value - 1);
  }

  return { pendingCount, isActive, start, done };
});
